import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { ArrowUpRight, BookOpen, Brain, Check, Lock } from "lucide-react";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { ListRow } from "../ui/ListRow";
import { Skeleton } from "../ui/Skeleton";
import { StatusMessage, mapApiError } from "../ui/StatusMessage";
import { ChapterDownloadButton } from "../student/ChapterDownloadButton";
import { UpgradeCard } from "../student/UpgradeCard";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import type { AccessObject, ChapterListItem } from "../../lib/chaptersApi";
import { listBundles, userIdFromToken } from "../../lib/offlineChapters";

type Props = { courseId: string; slug: string; tab: "notes" | "quiz"; semester?: number | null };

const total = (counts: Record<string, number>) => Object.values(counts).reduce((a, b) => a + b, 0);

function notesStatus(ch: ChapterListItem): string {
  const p = ch.progress?.notes_percent;
  if (p == null || p <= 0) return "Not started";
  return p >= 100 ? "Finished" : `${p}% read`;
}

function quizStatus(ch: ChapterListItem): string {
  const best = Object.values(ch.progress?.quiz_best ?? {}).filter((v): v is number => v != null);
  return best.length ? `Best score ${Math.round(Math.max(...best))}%` : "Not started";
}

function AccessNote({ access }: { access: AccessObject }) {
  if (access.state === "locked") return <Badge variant="outline"><Lock size={12} className="mr-1 inline" aria-hidden="true" />Premium</Badge>;
  if (access.state === "preview") return <Badge variant="info">Free preview</Badge>;
  return null;
}

const isLocked = (a: AccessObject) => a.state === "locked";

/** A chapter row: a link when the student can open it; a plain premium row when not (the card below carries the upgrade button). */
function ChapterRow({ to, icon, title, status, access }: { to: string; icon: ReactNode; title: string; status: string; access: AccessObject }) {
  const subtitle = <span className="flex flex-wrap items-center gap-1.5"><span>{isLocked(access) ? "Premium" : status}</span>{!isLocked(access) && <AccessNote access={access} />}</span>;
  return isLocked(access)
    ? <ListRow icon={<Lock size={18} />} title={title} subtitle={subtitle} className="opacity-70" />
    : <ListRow to={to} icon={icon} title={title} subtitle={subtitle} action={<ArrowUpRight size={18} className="text-muted-foreground" aria-hidden="true" />} />;
}

/** Chapters of one course in source order: Notes (progress, download, continue) or Quiz (only chapters that have questions). */
export function CourseChapterLists({ courseId, slug, tab, semester }: Props) {
  const { tokens } = useAuth();
  const token = tokens?.accessToken ?? "";
  const userId = userIdFromToken(token);
  const [items, setItems] = useState<ChapterListItem[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState<Record<string, number>>({});
  const [last, setLast] = useState<{ chapter_id: string; slide_id: string } | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setError(null);
    try {
      const [list, progress] = await Promise.all([api.student.chapters.list(token, courseId), api.student.chapters.progress(token).catch(() => null)]);
      setItems([...list.items].sort((a, b) => a.order - b.order));
      setLast(progress?.last_read && progress.last_read.course_id === courseId ? progress.last_read : null);
    } catch (e) {
      setError(e);
    }
  }, [token, courseId]);

  const loadSaved = useCallback(async () => {
    if (!userId) return;
    const bundles = await listBundles(userId).catch(() => []);
    setSaved(Object.fromEntries(bundles.map((b) => [b.chapter_id, b.version])));
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    void loadSaved();
  }, [loadSaved]);

  const quizItems = useMemo(() => (items ?? []).filter((c) => total(c.quiz.counts) > 0), [items]);
  const resume = useMemo(() => {
    if (!items?.length) return null;
    const open = items.filter((c) => c.notes.access.state !== "locked");
    const fromLast = last ? open.find((c) => c.id === last.chapter_id) : undefined;
    if (fromLast && last) return { chapter: fromLast, slide: last.slide_id, started: true };
    const started = open.find((c) => c.progress?.last_position);
    if (started?.progress?.last_position) return { chapter: started, slide: started.progress.last_position.slide_id, started: true };
    return open[0] ? { chapter: open[0], slide: null, started: false } : null;
  }, [items, last]);

  if (error) return <StatusMessage variant={mapApiError(error).variant} title={mapApiError(error).title} message={mapApiError(error).message} action={{ label: "Try again", onClick: () => void load() }} />;
  if (items === null) return <Skeleton className="h-40 w-full" />;
  if (items.length === 0) return <StatusMessage variant="info" title="No chapters yet" message="Chapters for this course will appear here once they are published." />;

  if (tab === "quiz") {
    return quizItems.length === 0 ? (
      <StatusMessage variant="info" title="No quizzes yet" message="Open Notes to read this course. Quizzes appear here when a chapter has questions." />
    ) : (
      <div className="space-y-3">
        <ul className="space-y-2">
          {quizItems.map((ch) => (
            <li key={ch.id}>
              <ChapterRow to={`/student/study/${slug}/chapters/${ch.id}?tab=quiz`} icon={<Brain size={18} />} title={`Chapter ${ch.order} · ${ch.title}`} status={quizStatus(ch)} access={ch.quiz.access} />
            </li>
          ))}
        </ul>
        {quizItems.some((c) => isLocked(c.quiz.access)) && <UpgradeCard message="Some quizzes in this course are part of premium. Upgrade to practise every chapter." semester={semester} />}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {resume && (
        <Button to={`/student/study/${slug}/chapters/${resume.chapter.id}/notes${resume.slide ? `?slide=${resume.slide}` : ""}`} trailingArrow>
          {resume.started ? `Continue reading · Chapter ${resume.chapter.order}` : "Start reading"}
        </Button>
      )}
      <ul className="space-y-2">
        {items.map((ch) => (
          <li key={ch.id} className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <ChapterRow
                to={`/student/study/${slug}/chapters/${ch.id}/notes${ch.progress?.last_position ? `?slide=${ch.progress.last_position.slide_id}` : ""}`}
                icon={(ch.progress?.notes_percent ?? 0) >= 100 ? <Check size={18} /> : <BookOpen size={18} />}
                title={`Chapter ${ch.order} · ${ch.title}`}
                status={notesStatus(ch)}
                access={ch.notes.access}
              />
            </div>
            {ch.offline_allowed && (
              <ChapterDownloadButton chapterId={ch.id} chapterTitle={ch.title} version={ch.version} savedVersion={saved[ch.id] ?? null} token={token} userId={userId} onSaved={() => void loadSaved()} />
            )}
          </li>
        ))}
      </ul>
      {items.some((c) => isLocked(c.notes.access)) && <UpgradeCard message="Some chapters of this course are part of premium. Upgrade to read every chapter." semester={semester} />}
    </div>
  );
}
